import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Megaphone, Sparkles, Wallet, Plus, Loader2, Download, Trash2, Video, Image as ImageIcon, Clock,
  AlertTriangle, RefreshCw, Copy, Check, ArrowRight,
} from 'lucide-react';
import { API_URL } from '../../config';
import TeckiaCMStudioModal from '../shared/TeckiaCMStudioModal';

// TECKIA PUB : le studio de creation de TeckiA CM (photos, bibliotheque de modeles, prompt libre, IA au choix, images et videos) SANS Facebook.
// Rien n'est publie : on cree, on previsualise, on telecharge. Le solde est celui du Video Studio. Cote serveur : client virtuel « pub_me » (voir cm_routes.py).
const CLIENT = 'pub_me';
const MEDIA_RETENTION_DAYS = 14;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json',
});

const STATUS = {
  generating: ['Génération en cours…', 'text-blue-300 bg-blue-500/10 border-blue-400/20'],
  pending_validation: ['Prête', 'text-emerald-300 bg-emerald-500/10 border-emerald-400/20'],
  ready: ['Enregistrée', 'text-emerald-300 bg-emerald-500/10 border-emerald-400/20'],
  failed: ['Échec · remboursée', 'text-red-300 bg-red-500/10 border-red-400/20'],
  rejected: ['Écartée', 'text-gray-400 bg-gray-500/10 border-gray-400/20'],
};

const daysRemaining = (createdAt) => {
  if (!createdAt) return null;
  return MEDIA_RETENTION_DAYS - Math.floor((Date.now() - new Date(createdAt).getTime()) / 86400000);
};

export default function PubPanel({ onTopup, onCreditsChange }) {
  const [cfg, setCfg] = useState({});
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [videoCount, setVideoCount] = useState(0);
  const [maxVideos, setMaxVideos] = useState(25);
  const [showStudio, setShowStudio] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const [busy, setBusy] = useState('');
  const pollRef = useRef(null);

  const loadConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/cm/config/${CLIENT}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        setCfg(data.config || {});
        setBalance(data.credit_balance || 0);
      } else setError(data.message || 'Chargement impossible.');
    } catch (e) { setError('Chargement impossible.'); }
  }, []);

  const loadPosts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/cm/posts/${CLIENT}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        setPosts(data.posts || []);
        setVideoCount(data.video_count || 0);
        setMaxVideos(data.max_stored_videos || 25);
      }
    } catch (e) { /* silencieux */ }
  }, []);

  useEffect(() => {
    (async () => { await Promise.all([loadConfig(), loadPosts()]); setLoading(false); })();
  }, [loadConfig, loadPosts]);

  // tant qu'une creation est en cours, la liste se met a jour toute seule
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (posts.some(p => p.status === 'generating')) {
      pollRef.current = setInterval(() => { loadPosts(); loadConfig(); }, 6000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [posts, loadPosts, loadConfig]);

  const refreshAll = () => { loadPosts(); loadConfig(); if (onCreditsChange) onCreditsChange(); };

  const deletePost = async (id) => {
    if (!window.confirm('Supprimer définitivement cette création et son média ? Cette action est irréversible.')) return;
    setBusy(id);
    try {
      await fetch(`${API_URL}/api/cm/posts/${CLIENT}/${id}`, { method: 'DELETE', headers: authHeaders() });
      await loadPosts();
    } finally { setBusy(''); }
  };

  const copyCaption = async (p) => {
    try {
      await navigator.clipboard.writeText(p.caption || '');
      setCopied(p.id);
      setTimeout(() => setCopied(''), 1800);
    } catch (e) { /* silencieux */ }
  };

  const low = balance <= 0;
  const tight = !low && balance < 5000;
  const tone = low ? 'border-red-500/30 from-red-600/15 text-red-300' : tight ? 'border-amber-500/30 from-amber-500/15 text-amber-300' : 'border-emerald-500/25 from-emerald-500/15 text-emerald-300';

  if (loading) {
    return <div className="flex items-center justify-center gap-2 text-gray-400 text-sm py-16"><Loader2 className="w-4 h-4 animate-spin text-purple-400" /> Chargement de TeckiA Pub…</div>;
  }

  return (
    <div className="space-y-5 max-w-3xl mx-auto">
      <div className="relative overflow-hidden rounded-2xl border border-purple-500/25 p-6 shadow-[0_0_50px_rgba(147,51,234,0.12)]" style={{ background: 'linear-gradient(155deg, rgba(147,51,234,0.20), rgba(20,10,36,0.96) 55%, rgba(219,39,119,0.12))' }}>
        <div className="flex items-center gap-2.5 mb-1.5">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#9333EA,#DB2777)', boxShadow: '0 0 20px rgba(168,85,247,.5)' }}><Megaphone className="w-5 h-5 text-white" /></div>
          <div>
            <h2 className="text-white font-black text-lg leading-none">TeckiA Pub</h2>
            <div className="text-purple-200/80 text-[11px] mt-1">Le studio de création de TeckiA CM, sans compte Facebook</div>
          </div>
        </div>
        <p className="text-gray-300 text-xs leading-relaxed mt-3">
          Créez vos <b className="text-white">publicités en image ou en vidéo</b> : vos photos, la bibliothèque de modèles ou votre propre idée, avec ou sans amélioration par l&apos;IA.
          Aperçu, légende, puis <b className="text-white">téléchargement</b> : vous publiez ensuite où vous voulez (Facebook, TikTok, WhatsApp, Instagram…).
        </p>
      </div>

      <div className={`rounded-2xl border bg-gradient-to-br to-transparent p-4 flex items-center justify-between gap-3 flex-wrap ${tone}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center"><Wallet className="w-5 h-5" /></div>
          <div>
            <div className="text-gray-400 text-[11px] uppercase tracking-wide">Votre solde</div>
            <div className="text-xl font-bold">{balance.toLocaleString('fr-FR')} Ar</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(low || tight) && <span className="text-[11px] text-gray-300 hidden sm:flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {low ? 'Solde insuffisant' : 'Solde bas'}</span>}
          <button onClick={() => onTopup && onTopup()} className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-semibold">
            <Plus className="w-3.5 h-3.5" /> Recharger
          </button>
        </div>
      </div>

      {error && <div className="text-red-300 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3.5 py-2.5">{error}</div>}

      <button
        onClick={() => setShowStudio(true)}
        className="group w-full relative overflow-hidden flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-fuchsia-600/25 via-purple-600/25 to-blue-600/25 border border-purple-400/40 hover:border-purple-300/70 transition"
      >
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-fuchsia-500 via-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-purple-900/40">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="text-white text-sm font-semibold">Créer une pub</div>
          <div className="text-gray-400 text-xs">Photos, modèles de la bibliothèque, prompt libre, image ou vidéo, IA au choix</div>
        </div>
        <ArrowRight className="w-4 h-4 text-purple-300 flex-shrink-0 group-hover:translate-x-0.5 transition" />
      </button>

      {showStudio && (
        <TeckiaCMStudioModal
          clientId={CLIENT}
          cfg={cfg}
          creditBalance={balance}
          pub
          onTopup={onTopup}
          onClose={() => { setShowStudio(false); refreshAll(); }}
          onPublished={refreshAll}
        />
      )}

      <div>
        <div className="flex items-center justify-between mb-2.5">
          <span className="text-white text-sm font-bold">Mes pubs</span>
          <div className="flex items-center gap-3">
            <span className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-full ${videoCount >= maxVideos ? 'bg-red-500/15 text-red-300' : 'bg-white/5 text-gray-400'}`}>
              <Video className="w-3 h-3" /> {videoCount}/{maxVideos} vidéos
            </span>
            <button onClick={refreshAll} title="Actualiser" className="text-gray-400 hover:text-white"><RefreshCw className="w-3.5 h-3.5" /></button>
          </div>
        </div>

        {posts.length > 0 && (
          <div className="flex items-start gap-2 text-xs bg-blue-500/10 text-blue-300 rounded-lg p-2.5 mb-2.5">
            <Clock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>Vos images et vidéos sont conservées {MEDIA_RETENTION_DAYS} jours, puis supprimées automatiquement. Téléchargez celles que vous voulez garder.</span>
          </div>
        )}
        {videoCount >= maxVideos && (
          <div className="flex items-start gap-2 text-xs bg-red-500/10 text-red-300 rounded-lg p-2.5 mb-2.5">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>Limite de {maxVideos} vidéos atteinte : supprimez-en une pour en créer une nouvelle.</span>
          </div>
        )}

        {posts.length === 0 ? (
          <div className="text-center py-10 rounded-2xl border border-dashed border-white/10 bg-white/[0.02]">
            <Megaphone className="w-8 h-8 text-purple-400/50 mx-auto mb-2" />
            <p className="text-gray-400 text-xs">Aucune pub pour l&apos;instant.<br />Cliquez sur « Créer une pub » pour commencer.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map(p => {
              const [label, cls] = STATUS[p.status] || [p.status, 'text-gray-400 bg-gray-500/10 border-gray-400/20'];
              const left = daysRemaining(p.created_at);
              const isVideo = p.media_kind === 'video';
              return (
                <div key={p.id} className="bg-black/25 border border-white/10 rounded-2xl p-3 flex gap-3">
                  {p.image_url ? (
                    isVideo
                      ? <video src={p.image_url} controls playsInline className="w-28 h-28 sm:w-32 sm:h-32 rounded-xl object-cover bg-black flex-shrink-0" />
                      : <a href={p.image_url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0"><img src={p.image_url} alt="" className="w-28 h-28 sm:w-32 sm:h-32 rounded-xl object-cover" /></a>
                  ) : (
                    <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-xl bg-black/40 flex items-center justify-center flex-shrink-0">
                      {p.status === 'generating' ? <Loader2 className="w-6 h-6 text-purple-400 animate-spin" /> : isVideo ? <Video className="w-6 h-6 text-gray-600" /> : <ImageIcon className="w-6 h-6 text-gray-600" />}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${cls}`}>{label}</span>
                        <span className="text-gray-500 text-[10px]">{isVideo ? '🎬 Vidéo' : '🖼 Image'} · {new Date(p.created_at).toLocaleString('fr-FR')}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {p.image_url && (
                          <a href={p.image_url} download={`teckia_pub_${p.id}.${isVideo ? 'mp4' : 'jpg'}`} target="_blank" rel="noopener noreferrer" title="Télécharger" className="flex items-center gap-1 text-[11px] font-semibold text-blue-300 hover:text-blue-200">
                            <Download className="w-3.5 h-3.5" /> Télécharger
                          </a>
                        )}
                        <button onClick={() => deletePost(p.id)} disabled={busy === p.id} title="Supprimer définitivement" className="text-gray-500 hover:text-red-400 disabled:opacity-50"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                    {p.image_url && left !== null && <div className="text-gray-600 text-[10px] mb-1">{left > 0 ? `Expire dans ${left} jour${left > 1 ? 's' : ''}` : 'Expire bientôt'}</div>}
                    {p.status === 'failed' && <p className="text-red-300 text-[11px] leading-snug">{p.error_message || 'La génération a échoué.'}</p>}
                    {p.caption && p.status === 'ready' && (
                      <div className="mt-1">
                        <p className="text-gray-300 text-xs leading-relaxed line-clamp-3">{p.caption}</p>
                        <button onClick={() => copyCaption(p)} className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-gray-400 hover:text-white">
                          {copied === p.id ? <><Check className="w-3 h-3 text-emerald-400" /> Copié</> : <><Copy className="w-3 h-3" /> Copier le texte</>}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
